import { useMemo, type ReactNode } from "react";
import { observer } from "mobx-react";
import { useScopedQuery } from "@/hooks/use-scoped-query";
import { timezonesQuery } from "@/queries/reference-lists";
import { GLOBAL_TENANT } from "@/queries/scope";
import { useStore } from "@/stores/store-context";
import { CommandSelect, type CommandSelectOption } from "@/components/ui/command-select";
import { useTranslation } from "@/i18n";
import type { ITimezoneOption } from "@/services/timezone.service";

const NO_OPTIONS: ITimezoneOption[] = [];

interface TimezoneSelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  /** Presentation only; value and searchable options retain their raw IANA identity. */
  triggerLabel?: ReactNode;
}

/** "UTC-05:00 — America/Toronto" — mirrors the legacy Select option label. */
function optionLabel(option: ITimezoneOption): string {
  return `${option.utc_offset} — ${option.label}`;
}

/** Split into lowercase tokens, treating "_" / "/" as separators so a search for
 *  "los angeles" or "toronto" hits the IANA id (America/Los_Angeles). */
function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[_/\s]+/)
    .filter(Boolean);
}

/** Searchable terms cmdk matches against (in addition to `name`): the city/region
 *  label, IANA identifier and the UTC/GMT offset labels — preserving the legacy
 *  custom match across city / IANA name / UTC offset. */
function searchKeywords(option: ITimezoneOption): string[] {
  return tokenize(`${option.label} ${option.value} ${option.utc_offset} ${option.gmt_offset}`);
}

/**
 * Searchable timezone picker. Users can type to filter the IANA timezone list
 * by city/region name (e.g. "Toronto" surfaces America/Toronto), by the IANA
 * identifier, or by the UTC/GMT offset. The selected `value` is the IANA
 * identifier exactly as the backend supplies and expects it.
 *
 * Thin wrapper over the shared `CommandSelect` base (single mode). The legacy
 * custom matcher is reproduced through cmdk's local filter by mapping each
 * timezone's searchable terms into `keywords`.
 */
export const TimezoneSelect = observer(function TimezoneSelect({
  id,
  value,
  onChange,
  disabled,
  className,
  triggerLabel,
}: TimezoneSelectProps) {
  const { timezoneService } = useStore();
  const { t } = useTranslation();
  // One shared reference list (NG-2803): every picker on the page, and every
  // later mount for five minutes, reads the same answer. A failure is logged by
  // the query client and leaves the list empty.
  const { query } = useScopedQuery(GLOBAL_TENANT, (scope) =>
    timezonesQuery(timezoneService, scope),
  );
  const options: ITimezoneOption[] = query.data ?? NO_OPTIONS;
  const isLoading = query.data === undefined && !query.isError;

  const commandOptions = useMemo<CommandSelectOption[]>(
    () =>
      options.map((option) => ({
        id: option.value,
        name: optionLabel(option),
        keywords: searchKeywords(option),
      })),
    [options],
  );

  // Fall back to the raw value so an unknown / not-yet-loaded tz still shows as
  // the selection: surface it as its own option when the list lacks it.
  const optionsWithFallback = useMemo<CommandSelectOption[]>(() => {
    if (!value || commandOptions.some((o) => o.id === value)) return commandOptions;
    return [{ id: value, name: value }, ...commandOptions];
  }, [commandOptions, value]);

  return (
    <CommandSelect
      mode="single"
      id={id}
      options={optionsWithFallback}
      value={value || null}
      onChange={(next) => onChange(next ?? "")}
      disabled={disabled || isLoading}
      placeholder={t("shared.timezone.placeholder")}
      searchPlaceholder={t("shared.timezone.search_placeholder")}
      emptyLabel={t("shared.timezone.no_matches")}
      triggerAriaLabel={t("shared.timezone.placeholder")}
      renderTriggerLabel={triggerLabel !== undefined ? () => triggerLabel : undefined}
      className={className}
    />
  );
});
